import { useCallback, useSyncExternalStore } from 'react';

/**
 * Frontend-only feature toggles — no backend round-trip.
 *
 * Resolution order (first hit wins):
 *  1. localStorage — `b1link:feature:<name>` set to 'true' / 'false'
 *     (written by the in-app toggle; also flippable from devtools)
 *  2. Vite env var `VITE_FEATURE_<NAME>` set to 'true' (per-environment default)
 *  3. OFF
 *
 * `useFrontendFeature` re-renders every subscribed component when the flag flips
 * (same pub/sub pattern as useOnboardingLayoutToggle) so the toggle applies live,
 * without a reload.
 */

const STORAGE_PREFIX = 'b1link:feature:';

export type FrontendFeature = 'stickyTables';

const ENV_KEYS: Record<FrontendFeature, string> = {
  stickyTables: 'VITE_FEATURE_STICKY_TABLES'
};

export const isFrontendFeatureEnabled = (feature: FrontendFeature): boolean => {
  try {
    const override = window.localStorage.getItem(`${STORAGE_PREFIX}${feature}`);
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to env default
  }

  return import.meta.env[ENV_KEYS[feature]] === 'true';
};

export const setFrontendFeature = (feature: FrontendFeature, enabled: boolean): void => {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${feature}`, String(enabled));
  } catch {
    // storage unavailable — the in-memory flip below still notifies subscribers
  }
  listeners.forEach((cb) => cb());
};

// ── Tiny pub/sub so every hook instance re-renders on toggle ──
const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useFrontendFeature(feature: FrontendFeature) {
  const enabled = useSyncExternalStore(
    subscribe,
    () => isFrontendFeatureEnabled(feature),
    () => false
  );

  const toggle = useCallback(() => {
    setFrontendFeature(feature, !isFrontendFeatureEnabled(feature));
  }, [feature]);

  return { enabled, toggle } as const;
}
